/**
 * One query object per list screen.
 *
 * The dashboards used to hand-build their request params at eight separate call sites, spread
 * across three unsynced state stores (the page's `filters`, ListTable's sortConfig/searchTerm and
 * ListLayout's currentPage/pageLength). Every reported bug came from that: paging after a sort
 * requested pageLength 10 again, an analytics tile's date range was wiped by the next sort, and a
 * search dropped the page size.
 *
 * The fix is that a screen keeps ONE query object and every interaction goes through
 * `mergeQuery`, so no interaction can drop a parameter it doesn't know about.
 */

/** Page sizes the UI offers. Kept in step with App\Traits\SortsAppointmentLists::PAGE_LENGTHS. */
export const PAGE_LENGTHS = [10, 25, 50, 100, 200];

/** Client default: 100 rows, soonest job first. */
export const DEFAULT_LIST_QUERY = {
  page: 1,
  pageLength: 100,
  sortBy: 'datetime',
  sortDirection: 'asc',
  search: '',
};

/**
 * Merge a patch into the current query.
 *
 * Any change other than paging returns to page 1 — otherwise a new filter or sort would be
 * applied while still asking for page 7, which lands the user on an empty table. A patch that
 * names `page` explicitly always wins.
 */
export const mergeQuery = (current = {}, patch = {}) => {
  const next = { ...current, ...patch };

  if (!('page' in patch)) {
    next.page = Object.keys(patch).length > 0 ? 1 : (current.page ?? 1);
  }

  return next;
};

/**
 * Direction for the next click on a column header: a column the list is not sorted by starts
 * ascending (earliest date, A-Z), and clicking the active column flips it.
 */
export const nextSortDirection = (query, sortBy) =>
  query?.sortBy === sortBy && query?.sortDirection === 'asc' ? 'desc' : 'asc';

/* ------------------------------------------------------------------------------------------
 * Remembering a screen's applied filters across navigation
 *
 * Client rule: filters stay intact when you come back from a job's view page, and RESET on a
 * page refresh — deliberately NOT held in local storage.
 *
 * So this is a plain module-scope Map. A module variable lives exactly as long as the loaded JS
 * bundle: it survives every in-app route change (the dashboard is unmounted and remounted when
 * you open a job and come back) and it is gone after a refresh, a hard reload, or logout — which
 * already calls window.location.reload() in userSlice.
 *
 * Why not the obvious alternatives:
 *  - a redux slice: store/index.js wraps the WHOLE root reducer in persistReducer with no
 *    whitelist/blacklist, so every slice is mirrored into localStorage `persist:root` and would
 *    survive a refresh — the exact thing we're told not to do.
 *  - sessionStorage: survives a refresh in the same tab.
 *  - URL query params: survive a refresh, and leak filters into shared links.
 * Keeping it in memory makes "resets on refresh" structural rather than a config setting someone
 * can quietly undo later. NEVER give this module a localStorage/sessionStorage backing.
 * ---------------------------------------------------------------------------------------- */

const screenState = new Map();

/** Applied state for a screen, or undefined on the first visit of this page session. */
export const readListState = (key) => screenState.get(key);

/** Called only from a screen's applyQuery, so the cache can only ever hold APPLIED state. */
export const writeListState = (key, state) => {
  screenState.set(key, state);
};

/** Reset must forget the remembered filters, or they would return on the next visit. */
export const clearListState = (key) => {
  screenState.delete(key);
};

/**
 * The form-facing subset of a query, used to re-seed react-hook-form on a restore.
 *
 * Must stay a whitelist: seeding the form with the whole query would put `page`, `pageLength` and
 * `sortBy` into form state, and onSubmit spreads form data into the patch — so a stale `page: 3`
 * would defeat mergeQuery's page reset and drop the user on an empty table.
 */
export const pickFormValues = (query = {}, keys = []) =>
  keys.reduce((acc, key) => (key in query ? { ...acc, [key]: query[key] } : acc), {});
